import { DollarSign } from 'lucide-react';
import Image from 'next/image';
import React, { useMemo } from 'react';
import { TriggerAmountSource } from 'src/modules/conversions/domain';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';
import { formatUnits } from 'viem';

import { Text } from '@/design-system/components/ui';
import { formatDecimalAmount, getFormattedAmountPerBaseCurrencyObject } from '@/utils/format';

// Helper function to format amount values - handles both BigInt (wei) and decimal values
const formatAmountValue = (
  amount: string | number | bigint | null | undefined,
  decimals: number,
  format: 'wei' | 'decimal' | 'auto' = 'auto',
): number => {
  if (amount === null || amount === undefined || amount === '') {
    return 0;
  }

  const amountStr = String(amount);

  // Explicit format handling
  if (format === 'decimal') {
    return Number(amountStr);
  }

  if (format === 'wei') {
    try {
      return Number(formatUnits(BigInt(amountStr), decimals));
    } catch (_error) {
      return Number(amountStr);
    }
  }

  // Auto detection (existing logic)
  // Check if the amount contains a decimal point - if so, it's already formatted
  if (amountStr.includes('.')) {
    return Number(amountStr);
  }

  // If decimals is 0 (like points), treat as regular number
  if (decimals === 0) {
    return Number(amountStr);
  }

  // For currency with decimals > 0 and no decimal point, assume it's in wei format
  // This handles cases like "123000000" with 6 decimals = 123 USDC
  try {
    return Number(formatUnits(BigInt(amountStr), decimals));
  } catch (_error) {
    // If BigInt conversion fails, fallback to regular number
    return Number(amountStr);
  }
};

interface Props {
  amount?: string | number | bigint | null;
  userType: 'affiliate' | 'endUser' | 'pool';
  payoutCurrencyDecimals: number;
  payoutCurrencyAddress: string;
  payoutCurrencySymbol: string;
  payoutCurrencyLogoUri?: string | null;
  payoutCurrencyChainId: number | undefined;
  payoutBaseCurrencySymbol: string;
  isFixedPayment: boolean;
  isPointsReward: boolean;
  hasPayoutBaseCurrency: boolean;
  /**
   * Format of the amount value:
   * - 'wei': Amount is in wei format (e.g., "123000000" with 6 decimals = 123 USDC)
   * - 'decimal': Amount is already formatted as decimal (e.g., "123.45")
   * - 'auto': Auto-detect based on decimal point presence (default)
   */
  amountFormat?: 'wei' | 'decimal' | 'auto';
  width?: string;
  poolCalculationDay?: string;
  poolDuration?: number;
  amountSource?: TriggerAmountSource;
  level?: number;
}

interface IVariableAmountProps {
  payoutCurrencyAddress: string;
  amount: string | number | bigint | null | undefined;
  hasPayoutBaseCurrency: boolean;
  payoutCurrencySymbol?: string;
  payoutCurrencyLogoUri?: string | null;
  payoutBaseCurrencySymbol: string;
  amountSource?: TriggerAmountSource;
  isPointsPayout: boolean;
}

interface IFixedAmountProps {
  payoutCurrencyAddress: string;
  amount: string | number | bigint | null | undefined;
  payoutCurrencyDecimals?: number;
  payoutCurrencySymbol?: string;
  payoutCurrencyLogoUri?: string | null;
  isPointsPayout: boolean;
  /**
   * Format of the amount value:
   * - 'wei': Amount is in wei format (e.g., "123000000" with 6 decimals = 123 USDC)
   * - 'decimal': Amount is already formatted as decimal (e.g., "123.45")
   * - 'auto': Auto-detect based on decimal point presence (default)
   */
  amountFormat?: 'wei' | 'decimal' | 'auto';
  className?: string;
  imageClassName?: string;
  textClassName?: string;
  textVariant?: 'body-sm' | 'body-md' | 'body-lg' | 'caption';
}

export const VariableAmount = ({
  amount,
  payoutCurrencyAddress,
  hasPayoutBaseCurrency,
  payoutBaseCurrencySymbol,
  payoutCurrencySymbol = '',
  payoutCurrencyLogoUri,
  amountSource,
  isPointsPayout,
}: IVariableAmountProps) => {
  // Handle null/undefined amount values
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }

  const { adjustedAmount, adjustedMultiplier, baseCurrencyImageSrc, action, isSameCurrency, pointsLabel } =
    getFormattedAmountPerBaseCurrencyObject(
      Number(amount),
      payoutCurrencySymbol,
      payoutBaseCurrencySymbol,
      amountSource,
      isPointsPayout,
    );

  const symbol = payoutCurrencySymbol;
  const isUSD = payoutBaseCurrencySymbol ? payoutBaseCurrencySymbol.toUpperCase() === 'USD' : false;

  return (
    <div className="tw:flex tw:items-center tw:gap-[0.3rem]">
      {!isSameCurrency &&
        (isPointsPayout ? (
          <div className="tw:w-5 tw:h-5 tw:relative">
            <Image src="/assets/svg/coin.svg" alt="points" fill />
          </div>
        ) : (
          <CurrencyIcon logoUri={payoutCurrencyLogoUri} name={symbol} size={20} />
        ))}
      <Text variant="body-md">
        {adjustedAmount} {!isSameCurrency ? (isPointsPayout ? pointsLabel : symbol) : null} {action}
      </Text>
      {!isSameCurrency && (hasPayoutBaseCurrency || isPointsPayout) && baseCurrencyImageSrc && !isUSD && (
        <div className="tw:w-5 tw:h-5 tw:relative">
          <Image src={baseCurrencyImageSrc} alt={payoutCurrencyAddress} fill />
        </div>
      )}
      {isSameCurrency && !isPointsPayout && !isUSD && (
        <CurrencyIcon logoUri={payoutCurrencyLogoUri} name={symbol} size={20} />
      )}
      <Text variant="body-md" className="tw:flex tw:items-center">
        {isUSD && <DollarSign width={16} height={16} />}
        {(hasPayoutBaseCurrency || isPointsPayout) && adjustedMultiplier && adjustedMultiplier}{' '}
        {(hasPayoutBaseCurrency || isSameCurrency) && (!isPointsPayout || isUSD) && payoutBaseCurrencySymbol}
        {isPointsPayout && isSameCurrency && pointsLabel}
      </Text>
    </div>
  );
};

export const FixedAmount = ({
  amount,
  payoutCurrencyAddress,
  payoutCurrencyDecimals = 0,
  payoutCurrencySymbol = '',
  payoutCurrencyLogoUri,
  isPointsPayout,
  amountFormat = 'auto',
  className,
  imageClassName,
  textClassName,
  textVariant,
}: IFixedAmountProps) => {
  // Handle null/undefined amount values
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }

  const symbol = isPointsPayout ? (Number(amount) === 1 ? 'POINT' : 'POINTS') : payoutCurrencySymbol;
  const imageAlt = isPointsPayout ? 'points' : payoutCurrencyAddress;
  const decimals = isPointsPayout ? 0 : payoutCurrencyDecimals;

  const numericAmount = formatAmountValue(amount, decimals, amountFormat);
  const displayAmount = numericAmount > 0 && numericAmount < 0.001 ? '>0.001' : formatDecimalAmount(numericAmount);

  return (
    <div className={`tw:flex tw:items-center tw:gap-[0.3rem] ${className ?? ''}`}>
      <div className={`tw:w-5 tw:h-5 tw:relative ${imageClassName ?? ''}`}>
        {isPointsPayout ? (
          <Image src="/assets/svg/coin.svg" alt={imageAlt} fill />
        ) : (
          <CurrencyIcon logoUri={payoutCurrencyLogoUri} name={symbol} size={20} />
        )}
      </div>
      <Text variant={textVariant ?? 'body-md'} className={textClassName}>
        {displayAmount} {symbol}
      </Text>
    </div>
  );
};

export const FormattedAmount = ({
  amount,
  userType,
  payoutCurrencyAddress,
  payoutCurrencySymbol,
  payoutCurrencyLogoUri,
  payoutCurrencyDecimals,
  payoutBaseCurrencySymbol,
  isFixedPayment,
  isPointsReward,
  hasPayoutBaseCurrency,
  amountFormat = 'auto',
  width,
  poolDuration,
  poolCalculationDay,
  amountSource,
  level,
}: Props) => {
  const distributionBudget = useMemo(() => {
    if (!poolDuration) return '';

    if (poolDuration < 24) {
      return `Distribution budget every ${poolDuration} hour${poolDuration > 1 ? 's' : ''}`;
    } else if (poolDuration % 168 === 0) {
      const weeks = poolDuration / 168;
      return `Distribution budget every ${weeks} week${weeks > 1 ? 's' : ''}`;
    } else {
      const days = poolDuration / 24;
      return `Distribution budget every ${days} day${days > 1 ? 's' : ''}`;
    }
  }, [poolDuration, poolCalculationDay]);

  if (!amount || amount === '0') {
    return '';
  }

  const parseUserType =
    userType === 'affiliate'
      ? level
        ? level === 1
          ? 'referrers (Direct Referrals)'
          : `referrers (Level ${level} Referrals)`
        : 'referrers'
      : 'end users';

  return (
    <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
      <Text variant="body-md" color="muted" as="span" className="tw:whitespace-nowrap" style={{ width }}>
        {userType == 'pool' ? distributionBudget : `To ${parseUserType}`}
      </Text>
      {isFixedPayment ? (
        <FixedAmount
          isPointsPayout={isPointsReward}
          amount={amount}
          payoutCurrencyDecimals={payoutCurrencyDecimals}
          payoutCurrencyAddress={payoutCurrencyAddress}
          payoutCurrencySymbol={payoutCurrencySymbol}
          payoutCurrencyLogoUri={payoutCurrencyLogoUri}
          amountFormat={amountFormat}
        />
      ) : (
        <VariableAmount
          isPointsPayout={isPointsReward}
          hasPayoutBaseCurrency={hasPayoutBaseCurrency}
          payoutCurrencyAddress={payoutCurrencyAddress}
          payoutCurrencySymbol={payoutCurrencySymbol}
          payoutCurrencyLogoUri={payoutCurrencyLogoUri}
          payoutBaseCurrencySymbol={payoutBaseCurrencySymbol}
          amount={amount}
          amountSource={amountSource}
        />
      )}
    </div>
  );
};
